import React, { useMemo } from "react";
import ProductArtworkPreview from "./ProductArtworkPreview";
import {
    normalizeMediaUrl,
    resolveProductColorImage,
    resolveProductDisplayImage,
} from "../../services/productService";
import { getPackIncludedProducts, Pack, PackProduct } from "../../services/packService";
import PlaceholderImg from "../../assets/img/e-commerce/placeholder-img.png";

const MAX_COLLAGE_ITEMS = 6;

type FrameStyle = "sticker" | "card" | "polaroid" | "phone" | "product" | "square" | "portrait";

/**
 * Display-collage frame order (Creator Pack reference):
 * sticker → rounded tin/card → photo print → phone → product → square
 */
const FRAME_BY_INDEX: FrameStyle[] = [
    "sticker",
    "card",
    "polaroid",
    "phone",
    "product",
    "square",
];

type PackShowcaseCollageProps = {
    pack?: Pack | null;
    products?: PackProduct[];
    favouriteImg?: string | null;
    fallbackImage?: string;
    className?: string;
};

function cleanLabel(value: unknown): string {
    if (typeof value !== "string") return "";
    const trimmed = value.trim();
    if (!trimmed || trimmed === "[object Object]") return "";
    return trimmed;
}

function getColorLabel(color: unknown): string {
    if (!color) return "";
    if (typeof color === "string") return cleanLabel(color);
    if (typeof color !== "object") return "";
    const obj = color as Record<string, unknown>;
    const nestedColor = obj.color;
    const nestedColour = obj.colour;
    return (
        cleanLabel(obj.name) ||
        cleanLabel(typeof nestedColor === "string" ? nestedColor : (nestedColor as any)?.name) ||
        cleanLabel(typeof nestedColour === "string" ? nestedColour : (nestedColour as any)?.name) ||
        cleanLabel(obj.label) ||
        cleanLabel(obj.value) ||
        ""
    );
}

function isUsableImageUrl(value: unknown): value is string {
    if (typeof value !== "string") return false;
    const trimmed = value.trim();
    if (!trimmed) return false;
    const lower = trimmed.toLowerCase();
    return lower !== "null" && lower !== "undefined" && lower !== "[object object]";
}

function resolvePackProductImage(entry: PackProduct): string {
    const product = entry.product || entry;
    const adminColor =
        getColorLabel(entry.color) ||
        getColorLabel(entry.units?.[0]?.color) ||
        cleanLabel(String(entry.units?.[0]?.color || entry.color || ""));

    const colorImage =
        resolveProductColorImage(product, adminColor) ||
        resolveProductColorImage(entry, adminColor);

    const candidates = [
        colorImage,
        resolveProductDisplayImage(product),
        resolveProductDisplayImage(entry),
        normalizeMediaUrl(entry.picture),
        normalizeMediaUrl(entry.product?.picture),
        normalizeMediaUrl((entry.product as any)?.imgURL),
        normalizeMediaUrl((entry.product as any)?.image),
        PlaceholderImg,
    ];

    return candidates.find(isUsableImageUrl) || PlaceholderImg;
}

function resolveFrameStyle(entry: PackProduct, index: number): FrameStyle {
    const name = String(entry.name || entry.product?.name || "").toLowerCase();
    const type = String((entry.product as any)?.type || (entry as any)?.type || "").toLowerCase();
    const sku = String((entry.product as any)?.sku_code || (entry as any)?.sku_code || "").toLowerCase();
    const haystack = `${name} ${type} ${sku}`;

    // Real product type wins when it maps cleanly to a collage frame.
    if (/sticker/.test(haystack)) return "sticker";
    if (/phone|case|iphone|android/.test(haystack)) return "phone";
    if (/magnet|badge|coaster/.test(haystack)) return "card";
    if (/print|poster|photo|polaroid/.test(haystack)) return "polaroid";

    // Tall portrait products → 1:1.5 frame.
    if (/tote|bag|print|poster|canvas|frame/.test(haystack) && index >= 2) {
        return "portrait";
    }

    // Merchandise mockups keep product frame (no fake phone/print chrome).
    if (
        /mug|cup|tumbler|cap|hat|shirt|tee|hoodie|apparel|tshirt|t-shirt|tote|bag|bottle|flask|sock|pouch/.test(
            haystack
        )
    ) {
        return "product";
    }

    // Unknown pack items: prefer clean product mockup over decorative frames.
    if (entry.product || entry.picture || entry.product_id) {
        return "product";
    }

    return FRAME_BY_INDEX[index % FRAME_BY_INDEX.length];
}

/** Extra layout classes (e.g. 1:1.5 portrait slot for 3rd item only). */
function resolveRatioClass(index: number, total: number, frame: FrameStyle): string {
    // Only force tall frame when the product actually uses a portrait/print/phone chrome.
    if (frame === "portrait" || frame === "phone" || frame === "polaroid") {
        return "ratio-1-1-5";
    }
    if (total === 3 && index === 2 && frame !== "product" && frame !== "sticker") {
        return "ratio-1-1-5";
    }
    return "";
}

/** Even index → #fff, odd index → #fff */
function pickTileBackground(index: number): "#fff" | "#fff" {
    return index % 2 === 0 ? "#fff" : "#fff";
}

const PackShowcaseCollage = ({
    pack,
    products,
    favouriteImg = null,
    fallbackImage,
    className = "",
}: PackShowcaseCollageProps) => {
    const productDisplay = useMemo(() => {
        const list =
            Array.isArray(products) && products.length > 0
                ? products
                : getPackIncludedProducts(pack);
        return list.slice(0, MAX_COLLAGE_ITEMS);
    }, [pack, products]);

    const artSource =
        typeof favouriteImg === "string" ? favouriteImg.trim() : "";

    if (productDisplay.length === 0) {
        if (!fallbackImage) return null;
        return (
            <img
                src={fallbackImage}
                className={`packShowcaseImage ${className}`.trim()}
                alt={typeof pack?.name === "string" ? pack.name : "Pack"}
            />
        );
    }

    const displayLength = Math.min(MAX_COLLAGE_ITEMS, Math.max(2, productDisplay.length));
    const countClass =
        productDisplay.length === 1
            ? "count-1 productDisplay-1"
            : `count-${displayLength} productDisplay-${displayLength}`;

    return (
        <div
            className={`packShowcaseCollage ${countClass} ${className}`.trim()}
            data-product-display-length={productDisplay.length}
            aria-hidden="true"
        >
            {productDisplay.map((entry, index) => {
                const productName = entry.name || entry.product?.name || "Included product";
                const productImage = resolvePackProductImage(entry);
                const productSku =
                    (entry.product as any)?.sku_code ||
                    (entry as any)?.sku_code;
                const productPlacement =
                    (entry.product as any)?.artwork_placement ||
                    (entry.product as any)?.artworkPlacement ||
                    (entry.product as any)?.print_area ||
                    (entry as any)?.artwork_placement ||
                    (entry as any)?.print_area;
                const frame = resolveFrameStyle(entry, index);
                const ratioClass = resolveRatioClass(index, productDisplay.length, frame);
                const isStickerFrame = frame === "sticker";
                const previewProductImage =
                    isStickerFrame && artSource ? artSource : productImage;
                const previewArtwork = isStickerFrame ? undefined : artSource || undefined;
                const itemKey = String(
                    entry.product_id || entry.product?.id || entry.id || `pack-item-${index}`
                );
                const tileBg = pickTileBackground(index);

                return (
                    <div
                        key={`${itemKey}-${index}`}
                        className={`packShowcaseCollageItem item-${index} frame-${frame} ${ratioClass}`.trim()}
                        style={{ backgroundColor: tileBg }}
                    >
                        <div className="packShowcaseCollageFrameInner">
                            {previewArtwork || isStickerFrame ? (
                                <ProductArtworkPreview
                                    productImage={previewProductImage}
                                    artworkImage={previewArtwork}
                                    itemKey={`pack-collage-${itemKey}`}
                                    productName={productName}
                                    skuCode={productSku}
                                    placement={productPlacement}
                                    exportResult={false}
                                    className="packShowcaseCollagePreview"
                                    style={{ maxHeight: "none", height: "auto", borderRadius: 0 }}
                                />
                            ) : (
                                <img
                                    src={productImage}
                                    alt=""
                                    className="packShowcaseCollagePreview"
                                />
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default PackShowcaseCollage;
